'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { type Dispatch, type SetStateAction, useCallback } from 'react'
import { type Resolver, useForm } from 'react-hook-form'
import type { z } from 'zod'
import Input from '@/components/Form/Input'
import MaskedInput from '@/components/Form/MaskedInput'
import Select from '@/components/Form/Select'
import Modal from '@/components/Layout/Modal'
import { CAREER_OPTIONS, CAREER_PJ_OPTIONS, CUSTOMER_PF_FIELD_MAP } from '@/constants/customer'
import { CELL_PHONE_MASK, CNPJ_MASK, CPF_MASK } from '@/constants/masks'
import { useSessionRefresh } from '@/hooks/business/auth/useSessionRefresh'
import { getCustomerCellPhone, useCustomer, useUpdateCustomer } from '@/hooks/business/customer/useCustomer'
import { useFormAction } from '@/hooks/shared/useFormAction'
import {
    CustomerPFFormSchema,
    CustomerPFWithPasswordFormSchema,
    CustomerPJCompleteFormSchema,
    type ICustomerPFWithPasswordForm,
} from '@/schemas/customer/customerForm'

interface EditUserModalProps {
    open: boolean
    closeBtn?: boolean
    setIsEditUserModalOpen: Dispatch<SetStateAction<boolean>>
}

/** Valores do formulário: com senha só para o cliente sem cadastro completo. */
type IFormInput = z.input<typeof CustomerPFWithPasswordFormSchema>

const FIELD_MAP = { ...CUSTOMER_PF_FIELD_MAP, Password: 'password' } as const

type IFormResolver = Resolver<IFormInput, unknown, ICustomerPFWithPasswordForm>

/**
 * Validação pelo tipo do cliente: PJ com CNPJ (sem senha), PF com ou sem a criação da senha. Sem senha, a saída é um
 * subconjunto do tipo com senha.
 */
function formResolver(isCompany: boolean, needsPassword: boolean): IFormResolver {
    if (needsPassword) return zodResolver(CustomerPFWithPasswordFormSchema)
    return (isCompany
        ? zodResolver(CustomerPJCompleteFormSchema)
        : zodResolver(CustomerPFFormSchema)) as unknown as IFormResolver
}

/**
 * Completar o cadastro (Navbar e checkout): o cliente do login sem conta do carrinho (sem nome) informa os dados e cria
 * a senha pelo `customer/update`; o que já tem nome só completa o que falta. O cliente PJ grava pelo
 * `customer/update-company-customer` (o `customer/update` grava o documento como CPF e a API recusa o CNPJ), com a razão
 * social e as inscrições do cadastro. Depois a sessão é renovada (`useSessionRefresh`), porque o token leva o nome do
 * cliente.
 */
const EditUserModal: React.FC<EditUserModalProps> = ({ open, closeBtn = true, setIsEditUserModalOpen }) => {
    const { customer: user } = useCustomer()

    if (!open || !user) return null

    return (
        <Modal
            isOpen={open}
            onClose={() => setIsEditUserModalOpen(false)}
            headerText="Complete seu cadastro para efetuar a compra"
        >
            <div className="flex flex-col w-full col-span-4 gap-6 overflow-auto">
                <EditUserForm closeBtn={closeBtn} setIsEditUserModalOpen={setIsEditUserModalOpen} />
            </div>
        </Modal>
    )
}

/** O formulário monta só com o modal aberto: os valores iniciais são os do cliente nesse momento. */
const EditUserForm: React.FC<Omit<EditUserModalProps, 'open'>> = ({ closeBtn, setIsEditUserModalOpen }) => {
    const { customer: user, isCompany } = useCustomer()
    const needsPassword = !isCompany && !user?.name
    const [firstName = '', ...lastName] = (user?.name ?? '').split(' ')

    const {
        control,
        register,
        handleSubmit,
        setError,
        getValues,
        formState: { errors },
    } = useForm<IFormInput, unknown, ICustomerPFWithPasswordForm>({
        resolver: formResolver(isCompany, needsPassword),
        defaultValues: {
            firstName,
            lastName: lastName.join(' '),
            email: user?.email ?? '',
            phone: getCustomerCellPhone(user),
            cpf: user?.mainDocNumber ?? '',
            occupation: user?.occupation ?? '',
            ...(needsPassword ? { password: '', confirmPassword: '' } : {}),
        },
    })

    const { updateCustomer, updateCompanyCustomer } = useUpdateCustomer()
    const { refreshSession } = useSessionRefresh()

    // O handleSubmit entrega o valor já validado (a saída do schema).
    const action = useCallback(
        async (form: IFormInput) => {
            const data = form as ICustomerPFWithPasswordForm
            if (isCompany) {
                await updateCompanyCustomer({
                    corporateName: user?.name ?? '',
                    cnpj: data.cpf ?? '',
                    companyPhone: data.phone,
                    email: data.email,
                    occupation: data.occupation ?? '',
                    inscricaoEstadual: user?.inscricaoEstadual ?? '',
                    inscricaoMunicipal: user?.inscricaoMunicipal ?? '',
                })
            } else {
                await updateCustomer({ ...data, cpf: data.cpf ?? '', occupation: data.occupation ?? '' })
            }
            // Os dados já foram gravados: falha na renovação não reabre o formulário (a sessão segue com o token atual).
            await refreshSession().catch(() => null)
        },
        [isCompany, user, updateCustomer, updateCompanyCustomer, refreshSession],
    )
    const onSuccess = useCallback(() => setIsEditUserModalOpen(false), [setIsEditUserModalOpen])
    const { handler } = useFormAction(action, { setError, getValues, onSuccess, fieldMap: FIELD_MAP })

    const person = !isCompany
    const hasName = Boolean(user?.name)

    return (
        <form className="gap-3 px-4 pt-8 pb-2 md:grid md:grid-cols-2 max-md:h-[80vh]" onSubmit={handleSubmit(handler)}>
            <div>
                <Input
                    register={register('firstName')}
                    error={errors.firstName?.message}
                    labelText="Primeiro nome"
                    isRequired={true}
                    inputClass={hasName ? 'cursor-not-allowed' : ''}
                    readOnly={hasName}
                />
            </div>

            <div>
                <Input
                    register={register('lastName')}
                    error={errors.lastName?.message}
                    labelText="Sobrenome"
                    isRequired={true}
                    inputClass={hasName ? 'cursor-not-allowed' : ''}
                    readOnly={hasName}
                />
            </div>

            <div>
                <MaskedInput
                    mask={person ? CPF_MASK : CNPJ_MASK}
                    control={control}
                    inputClass={user?.mainDocNumber ? 'cursor-not-allowed!' : ''}
                    name="cpf"
                    labelText={person ? 'CPF*' : 'CNPJ'}
                    readOnly={Boolean(user?.mainDocNumber)}
                />
            </div>

            <div>
                <Input
                    register={register('email')}
                    error={errors.email?.message}
                    labelText="E-mail"
                    isRequired={true}
                    inputClass="cursor-not-allowed"
                    readOnly
                />
            </div>

            <div>
                <Select
                    register={register('occupation')}
                    error={errors.occupation?.message}
                    options={isCompany ? CAREER_PJ_OPTIONS : CAREER_OPTIONS}
                    labelText="Profissão"
                    className="text-black font-sora"
                />
            </div>

            <div>
                <MaskedInput
                    control={control}
                    inputClass={user?.telephones[0] ? 'cursor-not-allowed' : ''}
                    mask={CELL_PHONE_MASK}
                    name="phone"
                    id="phone"
                    labelText="Celular*"
                    readOnly={Boolean(user?.telephones[0])}
                />
            </div>
            {needsPassword ? (
                <>
                    <div>
                        <Input
                            register={register('password')}
                            error={errors.password?.message}
                            labelText="Senha*"
                            type="password"
                        />
                    </div>

                    <div>
                        <Input
                            register={register('confirmPassword')}
                            error={errors.confirmPassword?.message}
                            type="password"
                            labelText="Confirmar senha*"
                        />
                    </div>
                </>
            ) : null}

            <div className="flex gap-3 max-sm:h-[100px] max-md:mt-2 justify-center col-span-4 max-sm:flex-col-reverse max-sm:col-span-5">
                <div className="flex gap-3 max-sm:h-[100px] max-md:mt-2 justify-center col-span-4 max-sm:flex-col-reverse max-sm:col-span-5">
                    {closeBtn ? (
                        <button
                            className="p-2 px-6 text-white rounded-full cursor-pointer bg-secondary/50 hover:bg-secondary"
                            onClick={() => setIsEditUserModalOpen(false)}
                            type="button"
                        >
                            Fechar
                        </button>
                    ) : null}
                    <button
                        type="submit"
                        className="p-2 px-6 text-white rounded-full cursor-pointer bg-primary-green hover:bg-success"
                    >
                        Salvar
                    </button>
                </div>
            </div>
        </form>
    )
}

export default EditUserModal
